import React, { useState } from "react";

import { Box, Chip, TextField, Typography } from "@mui/material";

import { useWarningSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";

type Props = {
  maxTags?: number;
  maxTagChars?: number;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
};

const TagsSelector: React.FC<Props> = ({ maxTags, maxTagChars, tags, onTagsChange }) => {
  const warningSnackbar = useWarningSnackbar();

  const [tagInput, setTagInput] = useState("");

  const addNewTag = () => {
    if (tagInput.trim() === "") {
      setTagInput("");
      return;
    }

    if (maxTags && tags.length === maxTags) {
      warningSnackbar(`You can only add up to ${maxTags} tags.`);
      setTagInput("");
      return;
    }

    if (tags.some((tag) => tag.toLowerCase() === tagInput.trim().toLowerCase())) {
      warningSnackbar("Tag already exists.");
      setTagInput("");
      return;
    }

    setTagInput("");

    onTagsChange([...tags, tagInput.trim()]);
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (maxTagChars && value.length > maxTagChars) {
      warningSnackbar(`Tag length is limited to ${maxTagChars} characters.`);
      return;
    }
    setTagInput(value);
  };
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      addNewTag();
    }
  };

  const handleTagInputBlur = () => {
    addNewTag();
  };

  const handleDeleteTag = (tagToDelete: string) => {
    onTagsChange(tags.filter((tag) => tag !== tagToDelete));
  };

  return (
    <Box>
      <Box
        component="fieldset"
        p={0.8}
        sx={{
          flex: 1,
          color: "text.secondary",
          margin: 0,
          border: "1px solid",
          borderColor: "action.disabled",
          borderRadius: 1,
          position: "relative",
          "&:hover": {
            borderColor: "text.primary",
          },
          "&:focus-within": {
            borderColor: "transparent",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -2,
              right: -2,
              bottom: -2,
              left: -2,
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: "inherit",
            },
          },
        }}
      >
        <Typography
          component="legend"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 1,
            width: "max-content",
            position: "absolute",
            top: -1.5,
            left: 10,
            transform: "translateY(-50%)",
            bgcolor: "background.paper",
          }}
        >
          Tags
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          {tags.map((tag, index) => (
            <Chip
              key={`${tag}-${index}`}
              label={tag}
              data-testid={`tag-${tag}`}
              onDelete={() => {
                handleDeleteTag(tag);
              }}
            />
          ))}
          <TextField
            variant="standard"
            value={tagInput}
            onChange={handleTagInputChange}
            data-testid="tags-input"
            sx={{
              ml: 0.5,
              mb: -0.5,
              flexGrow: 1,
              width: "auto",
            }}
            slotProps={{
              input: {
                disableUnderline: true,
              },
            }}
            onKeyDown={handleTagInputKeyDown}
            onBlur={handleTagInputBlur}
          />
        </Box>
      </Box>
      {maxTagChars && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Each tag must have a maximum length of {maxTagChars} characters.
        </Typography>
      )}
    </Box>
  );
};

export default TagsSelector;
